import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { KeyIcon, LinkIcon, TrashIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import MobileLayout from '../../components/MobileLayout';

interface ApiKeyRow {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  createdAt: string;
}

interface ConnectedApp {
  id: string;
  clientName: string;
  redirectHost: string;
  createdAt: string;
  lastUsedAt: string | null;
}

const card = {
  background: 'var(--ds-bg-card)',
  border: '1px solid var(--ds-border)',
  color: 'var(--ds-text-primary)',
};

/** Personal API keys for the Plotweave API (and MCP), on paid plans. */
const ApiAccess: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [keys, setKeys] = useState<ApiKeyRow[] | null>(null);
  const [paid, setPaid] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [fresh, setFresh] = useState<string | null>(null);
  const [needsPlan, setNeedsPlan] = useState(false);
  const [apps, setApps] = useState<ConnectedApp[]>([]);
  // The MCP server's canonical address, as OAuth metadata names it: connectors must be given
  // exactly this (not, say, the www. host this page may have been opened on).
  const [mcpUrl, setMcpUrl] = useState(`${window.location.origin}/api/mcp`);
  const origin = window.location.origin;

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api-keys');
      setKeys(res.data.keys);
      setPaid(res.data.paid);
    } catch {
      setKeys([]);
      toast.error(t('apiAccess.loadError', 'Could not load your API keys'));
    }
  }, [t]);

  const loadApps = useCallback(async () => {
    try {
      const res = await axios.get('/oauth/grants');
      setApps(res.data);
    } catch {
      setApps([]);
    }
  }, []);

  useEffect(() => {
    load();
    loadApps();
    axios
      .get('/.well-known/oauth-protected-resource')
      .then((res) => typeof res.data?.resource === 'string' && setMcpUrl(res.data.resource))
      .catch(() => undefined);
  }, [load, loadApps]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('apiAccess.copied', 'Copied'));
    } catch {
      window.prompt(t('apiAccess.copyPrompt', 'Copy this:'), text);
    }
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post('/api-keys', { name: name.trim() });
      setFresh(res.data.key);
      setName('');
      load();
    } catch (err: any) {
      if (err?.response?.data?.code === 'plan_required') setNeedsPlan(true);
      else
        toast.error(
          err?.response?.data?.message || t('apiAccess.createError', 'Could not create the key'),
        );
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (key: ApiKeyRow) => {
    if (
      !window.confirm(
        t(
          'apiAccess.confirmRevoke',
          'Revoke "{{name}}"? Anything using it stops working straight away.',
          { name: key.name },
        ),
      )
    )
      return;
    try {
      await axios.delete(`/api-keys/${key.id}`);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('apiAccess.revokeError', 'Could not revoke'));
    }
  };

  const disconnect = async (app: ConnectedApp) => {
    if (
      !window.confirm(
        t(
          'apiAccess.confirmDisconnect',
          'Disconnect "{{name}}"? It loses access to your stories straight away.',
          { name: app.clientName },
        ),
      )
    )
      return;
    try {
      await axios.delete(`/oauth/grants/${app.id}`);
      loadApps();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || t('apiAccess.disconnectError', 'Could not disconnect'),
      );
    }
  };

  const free = !paid || needsPlan;

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--ds-border)' }}>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              aria-label={t('common.back', 'Back')}
              className="w-10 h-10 flex items-center justify-center rounded-full"
            >
              <ArrowLeftIcon className="w-5 h-5" style={{ color: 'var(--ds-text-primary)' }} />
            </button>
            <h1 className="ml-4 text-xl font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
              {t('apiAccess.title', 'API access')}
            </h1>
          </div>
        </div>

        <div className="p-4 space-y-4 max-w-2xl w-full mx-auto">
          <p className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
            {t(
              'apiAccess.intro',
              'Use your stories from your own tools: scripts, apps, or an AI assistant running your own model. A key can read and edit your stories (and ones shared with you) but never uses your Plotweave AI words.',
            )}
          </p>

          {free && (
            <div className="rounded-xl p-4 text-sm space-y-2" style={card}>
              <div>{t('apiAccess.planRequired', 'API access is part of every paid plan.')}</div>
              <button
                type="button"
                onClick={() => navigate('/subscription')}
                className="text-primary-400 underline"
              >
                {t('apiAccess.seePlans', 'See plans')}
              </button>
            </div>
          )}

          {fresh && (
            <div className="rounded-xl p-4 space-y-2" style={card}>
              <div className="text-sm font-semibold">
                {t('apiAccess.newKey', 'Your new key. Copy it now: it will not be shown again.')}
              </div>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 min-w-0 break-all text-xs p-2 rounded-lg border"
                  style={{ borderColor: 'var(--ds-border)' }}
                >
                  {fresh}
                </code>
                <button
                  type="button"
                  onClick={() => copy(fresh)}
                  aria-label={t('apiAccess.copy', 'Copy')}
                  className="p-2"
                >
                  <ClipboardDocumentIcon className="w-5 h-5" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => setFresh(null)}
                className="text-xs"
                style={{ color: 'var(--ds-text-secondary)' }}
              >
                {t('apiAccess.done', "I've saved it")}
              </button>
            </div>
          )}

          {!free && (
            <form onSubmit={create} className="flex flex-wrap gap-2">
              <input
                type="text"
                value={name}
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('apiAccess.namePlaceholder', 'Key name, e.g. "My laptop"')}
                className="flex-1 min-w-[12rem] px-3 py-2 rounded-lg border bg-transparent text-sm"
                style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
              />
              <button
                type="submit"
                disabled={creating || !name.trim()}
                className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-medium"
              >
                {t('apiAccess.create', 'Create key')}
              </button>
            </form>
          )}

          {keys && keys.length > 0 && (
            <ul className="space-y-2">
              {keys.map((k) => (
                <li key={k.id} className="rounded-xl p-3 flex items-center gap-3" style={card}>
                  <KeyIcon className="w-5 h-5 shrink-0 text-primary-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{k.name}</div>
                    <div className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                      <code>{k.prefix}…</code> ·{' '}
                      {k.lastUsedAt
                        ? t('apiAccess.lastUsed', 'last used {{when}}', {
                            when: new Date(k.lastUsedAt).toLocaleString(),
                          })
                        : t('apiAccess.neverUsed', 'never used')}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => revoke(k)}
                    aria-label={t('apiAccess.revoke', 'Revoke')}
                    title={t('apiAccess.revoke', 'Revoke')}
                  >
                    <TrashIcon className="w-5 h-5 text-red-400" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="rounded-xl p-4 space-y-2 text-sm" style={card}>
            <div className="font-semibold">
              {t('apiAccess.connectorTitle', 'Connect Claude or ChatGPT')}
            </div>
            <p style={{ color: 'var(--ds-text-secondary)' }}>
              {t(
                'apiAccess.connectorBody',
                'In claude.ai or ChatGPT, add a custom connector with this address. No key needed: you approve it here, and it shows up below until you disconnect it.',
              )}
            </p>
            <div className="flex items-center gap-2">
              <code
                className="flex-1 min-w-0 break-all text-xs p-2 rounded-lg border"
                style={{ borderColor: 'var(--ds-border)' }}
              >
                {mcpUrl}
              </code>
              <button
                type="button"
                onClick={() => copy(mcpUrl)}
                aria-label={t('apiAccess.copy', 'Copy')}
                className="p-2"
              >
                <ClipboardDocumentIcon className="w-5 h-5" />
              </button>
            </div>
            {apps.length > 0 && (
              <ul className="space-y-2 pt-1">
                {apps.map((a) => (
                  <li
                    key={a.id}
                    className="rounded-xl p-3 flex items-center gap-3 border"
                    style={{ borderColor: 'var(--ds-border)' }}
                  >
                    <LinkIcon className="w-5 h-5 shrink-0 text-primary-400" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.clientName}</div>
                      <div
                        className="text-xs truncate"
                        style={{ color: 'var(--ds-text-secondary)' }}
                      >
                        {a.redirectHost} ·{' '}
                        {a.lastUsedAt
                          ? t('apiAccess.lastUsed', 'last used {{when}}', {
                              when: new Date(a.lastUsedAt).toLocaleString(),
                            })
                          : t('apiAccess.neverUsed', 'never used')}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => disconnect(a)}
                      aria-label={t('apiAccess.disconnect', 'Disconnect')}
                      title={t('apiAccess.disconnect', 'Disconnect')}
                    >
                      <TrashIcon className="w-5 h-5 text-red-400" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl p-4 space-y-2 text-sm" style={card}>
            <div className="font-semibold">
              {t('apiAccess.mcpTitle', 'Connect an AI assistant (MCP)')}
            </div>
            <p style={{ color: 'var(--ds-text-secondary)' }}>
              {t(
                'apiAccess.mcpBody',
                'Add Plotweave to an MCP client that takes an API key (Claude Code, Cursor, or your own local model). It reads your stories and edits them using your own model, with automatic snapshots so its changes can be undone from History.',
              )}
            </p>
            <div className="flex items-center gap-2">
              <code
                className="flex-1 min-w-0 break-all text-xs p-2 rounded-lg border"
                style={{ borderColor: 'var(--ds-border)' }}
              >
                {mcpUrl}
              </code>
              <button
                type="button"
                onClick={() => copy(mcpUrl)}
                aria-label={t('apiAccess.copy', 'Copy')}
                className="p-2"
              >
                <ClipboardDocumentIcon className="w-5 h-5" />
              </button>
            </div>
            <p style={{ color: 'var(--ds-text-secondary)' }}>
              {t('apiAccess.mcpClaudeCode', 'For example, in Claude Code:')}
            </p>
            <code
              className="block text-xs p-2 rounded-lg border whitespace-pre-wrap break-all"
              style={{ borderColor: 'var(--ds-border)' }}
            >
              {`claude mcp add --transport http plotweave ${mcpUrl} --header "Authorization: Bearer pw_..."`}
            </code>
          </div>

          <div className="rounded-xl p-4 space-y-2 text-sm" style={card}>
            <div className="font-semibold">{t('apiAccess.howTitle', 'Using a key')}</div>
            <p style={{ color: 'var(--ds-text-secondary)' }}>
              {t(
                'apiAccess.howBody',
                "Send it as a bearer token. It works for your stories, chapters, scenes, characters, plot threads, outline, snapshots, exports and save conflicts. Plotweave's AI, inviting people and account settings stay in the app.",
              )}
            </p>
            <code
              className="block text-xs p-2 rounded-lg border whitespace-pre-wrap break-all"
              style={{ borderColor: 'var(--ds-border)' }}
            >
              {`curl -H "Authorization: Bearer pw_..." ${origin}/api/stories`}
            </code>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default ApiAccess;
